// useLiveBuyPulse — global realtime listener for new BUY surfaced_calls.
// Fires once per app load. Triggers:
//   1. Sonner toast in-app (clickable → Feed)
//   2. Tab title badge "(N) Decyzon" when tab is hidden
//   3. Fresh-count broadcast via window event for Feed pill
//
// Toast/UI consistency: before firing the toast we re-derive the action from
// the latest market_feed row. If UI gates downgrade it (edge taken, thin
// book, no profit room), the toast is suppressed — never advertise a BUY the
// card won't render as BUY.
//
// Mounted ONCE in App.tsx. No props.

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { deriveAction } from "@/lib/actionState";
import { coerceSourceOptions } from "@/lib/sourceOptions";

export const FRESH_BUY_EVENT = "decyzon:fresh-buy";
const TITLE_BASE = "Decyzon — The decision layer for prediction markets";

interface BuyRow {
  id: string;
  market_id: string;
  question: string;
  option_label: string | null;
  market_price_at_issue: number | null;
  platform: string;
  market_url: string | null;
  action_state: string;
}

function cents(p: number | null | undefined): string {
  if (p == null || !Number.isFinite(p)) return "";
  return `${Math.round(p * 100)}¢`;
}

export function useLiveBuyPulse() {
  const navigate = useNavigate();
  const hiddenCountRef = useRef(0);
  const sessionStartRef = useRef(Date.now());

  // Reset hidden count when tab becomes visible.
  useEffect(() => {
    const onVis = () => {
      if (!document.hidden) {
        hiddenCountRef.current = 0;
        document.title = TITLE_BASE;
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel("live-buy-pulse")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "surfaced_calls",
          filter: "action_state=eq.BUY",
        },
        async (payload) => {
          const row = payload.new as BuyRow;
          if (!row || row.action_state !== "BUY") return;
          // Ignore back-fills older than session start (defensive).
          const t = (payload as any).commit_timestamp
            ? Date.parse((payload as any).commit_timestamp)
            : Date.now();
          if (Number.isFinite(t) && t < sessionStartRef.current - 60_000) return;

          // CONSISTENCY GATE: re-derive from latest market_feed row. If UI
          // logic now downgrades (edge taken, thin book, no profit room),
          // suppress the toast — toast must equal final BUY.
          try {
            const { data: feed } = await supabase
              .from("market_feed")
              .select("decision, edge_label, edge_pct, market_probability, last_market_probability, estimated_probability, too_expensive_above, total_liquidity, sibling_divergence, velocity_ratio, delta_1h, delta_24h, confidence, end_date, side, best_option, forced_winner, why_short, options")
              .eq("market_id", row.market_id)
              .eq("platform", row.platform as "polymarket" | "kalshi" | "other")
              .order("scanned_at", { ascending: false })
              .limit(1)
              .maybeSingle();
            if (feed) {
              const view = deriveAction({
                decision: (feed as any).decision,
                edge_label: (feed as any).edge_label,
                edge_pct: (feed as any).edge_pct,
                market_probability: (feed as any).market_probability,
                last_market_probability: (feed as any).last_market_probability,
                estimated_probability: (feed as any).estimated_probability,
                too_expensive_above: (feed as any).too_expensive_above,
                total_liquidity: (feed as any).total_liquidity,
                sibling_divergence: (feed as any).sibling_divergence,
                velocity_ratio: (feed as any).velocity_ratio,
                delta_1h: (feed as any).delta_1h,
                delta_24h: (feed as any).delta_24h,
                confidence: (feed as any).confidence,
                end_date: (feed as any).end_date,
                side: (feed as any).side,
                best_option: (feed as any).best_option,
                forced_winner: (feed as any).forced_winner,
                why_short: (feed as any).why_short,
                angle: null,
                options: coerceSourceOptions((feed as any).options),
              });
              if (view.verb !== "BUY") return; // suppress — UI downgraded
            }
          } catch {
            // On error, fail safe: skip toast rather than risk inconsistency.
            return;
          }

          // 1) In-app toast
          const opt = row.option_label ? row.option_label.toUpperCase() : null;
          const price = cents(row.market_price_at_issue);
          const head = opt
            ? `NEW BUY · ${opt}${price ? ` @ ${price}` : ""}`
            : `NEW BUY${price ? ` @ ${price}` : ""}`;
          toast(head, {
            description: row.question,
            duration: 8000,
            action: {
              label: "View",
              onClick: () => navigate("/feed"),
            },
          });

          // 2) Tab title badge while hidden
          if (document.hidden) {
            hiddenCountRef.current += 1;
            document.title = `(${hiddenCountRef.current}) ${TITLE_BASE}`;
          }

          // 3) Broadcast for Feed pill / any listener
          window.dispatchEvent(
            new CustomEvent(FRESH_BUY_EVENT, { detail: { id: row.id, question: row.question } }),
          );
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [navigate]);
}
